import {
  useAcceptTermAndConditionMutation,
  useGetLatestActiveTermAndCondition,
} from '@exam-room/graphql';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { Button, Card, Form } from '../../../components';
import { useAuth } from '../../../providers';
import { PATHS } from '../../constants';

interface AcceptTermsAndConditionsInput {
  accept: boolean;
}

function TermsAndConditions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { examCardId } = useParams();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const { control, watch, setValue } = useForm<AcceptTermsAndConditionsInput>({
    defaultValues: {
      accept: false,
    },
  });
  const { accept } = watch();

  const { language } = i18n;

  const { data: termsAndConditionsData, loading: isTermsAndConditionsLoading } =
    useGetLatestActiveTermAndCondition();

  const [acceptTermsAndConditions, { loading: isAcceptingTermsAndConditions }] =
    useAcceptTermAndConditionMutation();

  const termsAndConditions =
    termsAndConditionsData?.cluGetLatestActiveTermAndCondition?.termAndCondition;
  const documentUrl = termsAndConditions?.documentUrl;

  const handleOnAccept = () => {
    acceptTermsAndConditions({
      variables: {
        acceptTermAndConditionInput: {
          termAndConditionId: termsAndConditions?.id || '',
          isAccepted: true,
        },
      },
      onCompleted: () => {
        const profileConfirmationPath = generatePath(PATHS.Exam.ProfileConfirmation, {
          examCardId,
        });
        navigate(profileConfirmationPath);
      },
      onError: (error) => {
        console.error('Error accepting terms and conditions:', error);
        Sentry.captureException(error, {
          tags: {
            feature: 'accept-terms-and-conditions',
          },
          extra: {
            error,
          },
        });
      },
    });
  };

  const handleOnReject = async () => {
    await acceptTermsAndConditions({
      variables: {
        acceptTermAndConditionInput: {
          termAndConditionId: termsAndConditions?.id || '',
          isAccepted: false,
        },
      },
      onError: (error) => {
        console.error('Error accepting terms and conditions:', error);
      },
    });
    await logout();
    navigate(PATHS.Login);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
    const isBottom = scrollHeight - scrollTop - clientHeight < 24;
    if (isBottom && !isScrolledToBottom) {
      setIsScrolledToBottom(true);
    }
  };

  const handleOnCheckboxChange = () => {
    setValue('accept', !accept);
  };

  useEffect(() => {
    const fetchContent = async () => {
      if (!documentUrl) {
        setContent('No terms and conditions available.');
        return;
      }
      let localizedUrl = documentUrl;
      if (documentUrl?.includes('{locale}')) {
        localizedUrl = documentUrl.replace('{locale}', language);
      }
      try {
        const { data } = await axios.get(localizedUrl);
        setContent(data);
      } catch (error) {
        console.error('Error fetching terms and conditions content:', error);
        setContent('No terms and conditions available.');
        Sentry.captureException(error, {
          tags: {
            feature: 'fetch-terms-and-conditions',
            url: localizedUrl,
          },
        });
      }
    };
    fetchContent();
  }, [documentUrl, language]);

  return (
    <Card className='mx-auto w-[1200px] gap-6'>
      <div
        className='border-border-divider bg-grey-50 flex max-h-[calc(100vh-300px)] flex-col gap-2 overflow-y-scroll rounded-md border-1 p-6'
        onScroll={handleScroll}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children, ...props }) => (
              <h1 className='text-center text-3xl font-bold' {...props}>
                {children}
              </h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 className='w-full text-xl font-bold' {...props}>
                {children}
              </h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 className='text-info text-center text-lg font-bold' {...props}>
                {children}
              </h3>
            ),
            h4: ({ children, ...props }) => (
              <h4 className='text-md w-full font-bold' {...props}>
                {children}
              </h4>
            ),
            p: ({ ...props }) => <p className='text-body1' {...props} />,
            em: ({ children }) => <p className='text-info text-center font-bold'>{children}</p>,
            ul: ({ ...props }) => <ul className='list-disc pl-6' {...props} />,
            ol: ({ ...props }) => <ol className='list-decimal pl-6' {...props} />,
            li: ({ ...props }) => <li className='text-body1' {...props} />,
            del: ({ children }) => <span className='text-danger underline'>{children}</span>,
            code: ({ children }) => <span className='underline'>{children}</span>,
          }}
        >
          {content}
        </Markdown>
      </div>
      <Form.Checkbox
        name='accept'
        control={control}
        required
        checked={accept}
        onChange={handleOnCheckboxChange}
        disabled={!isScrolledToBottom}
      >
        {t('termsAndConditions.acceptCheckbox')}
      </Form.Checkbox>
      <div className='flex flex-row justify-center gap-4'>
        <Button onClick={handleOnReject} size='lg' variant='outlined' className='w-[120px]'>
          {t('decline')}
        </Button>
        <Button
          onClick={handleOnAccept}
          size='lg'
          className='w-[120px]'
          disabled={!accept || isTermsAndConditionsLoading || isAcceptingTermsAndConditions}
        >
          {t('accept')}
        </Button>
      </div>
    </Card>
  );
}

const TermsAndConditionsRoute = {
  path: PATHS.Exam.TermsAndConditions,
  element: <TermsAndConditions />,
};

export default TermsAndConditionsRoute;
